import * as AsciiMath from 'ascii-math'
import * as Complex from 'complex.js'
import * as Polynomial from 'polynomial'
import * as PolyRoots from 'durand-kerner'

import AudioVisualizer from './AudioVisualizer'
import BodePlot from './BodePlot'
import ComplexRenderer, {RenderMode} from './ComplexRenderer'
import FilterPlayer from './FilterPlayer'
import PoleZeroPlot from './PoleZeroPlot'
import TransferFunction from './TransferFunction'
import FilterWizard from './FilterWizard'

Polynomial.setField('C')

const DEFAULT_EQUATION = '(z^2)/(1+z)'

class View {
  id: string
  createFunc: Function

  constructor(id: string, createFunc: Function) {
    this.id = id
    this.createFunc = createFunc
  }

  render() {
    const el = document.getElementById(this.id)
    // Make it visible.
    if (el) {
      el.style.display = 'block'
    }
    if (this.createFunc) {
      this.createFunc()
    }
  }
}

const availableViews = [
  new View('header'),
  new View('filter-wizard', createFilterWizardView),
  new View('equation', createEquationView),
  new View('transfer-function', createTransferFunctionView),
  new View('audio-player', createAudioPlayerView),
  new View('audio-visualizer', createAudioVisualizerView),
  new View('bode-plot', createBodePlotView),
  new View('pole-zero', createPoleZeroPlotView),
  new View('footer'),
]

console.log('Hello filter playground!')

let player: FilterPlayer
let renderer: ComplexRenderer
let polezero: PoleZeroPlot
let bode: BodePlot
let equationInput: HTMLTextAreaElement
let equationRender: HTMLDivElement
let mutable = true


function onLoad() {
  // If this is loaded in an iframe embed, add an 'embed' class to the body.
  if (window !== window.parent) {
    document.body.classList.add('embed')
  }
  // Parse relevant GET parameters to decide what to render.
  const params = new URLSearchParams(location.search)
  let viewParams = params.getAll('view')
  let views = []
  if (viewParams.length == 0) {
    views = availableViews
  } else {
    for (let view of availableViews) {
      if (viewParams.indexOf(view.id) != -1) {
        views.push(view)
      }
    }
    // Prevent scrolling.
    document.body.style.overflow = 'hidden'
  }
  mutable = (params.get('mutable') !== 'false')

  for (let view of views) {
    view.render()
  }
  // Get equation from GET params, falling back to the input box, then the default.
  const equation = params.get('equation') ||
    (equationInput && equationInput.value)|| DEFAULT_EQUATION

  const renderMode = RenderMode[params.get('tfmode')]
  if (renderer && renderMode) {
    renderer.setRenderMode(renderMode)
  }
  const muted = (params.get('muted') === 'true')
  if (muted && player) {
    player.setGain(0)
  }

  onEquationChanged(equation)
}

function createAudioPlayerView() {
  player = new FilterPlayer()
  player.play()
}


function createTransferFunctionView() {
  renderer = new ComplexRenderer('#transfer-function')

  const plotSelect = document.querySelector('#plot-type')
  plotSelect.addEventListener('input', onPlotTypeChanged)

  function onPlotTypeChanged() {
    const mode = RenderMode[plotSelect.value]
    console.log('onPlotTypeChanged', mode)
    renderer.setRenderMode(mode)
  }

  if (!mutable) {
    plotSelect.style.display = 'none'
  }
}

function createPoleZeroPlotView() {
  polezero = new PoleZeroPlot('#pole-zero', mutable)
  polezero.on('transferfunctionchange', onPoleZeroTF)

  const addPole = document.querySelector('#add-pole') as HTMLButtonElement
  addPole.addEventListener('click', () => { polezero.addPole() })

  const addZero = document.querySelector('#add-zero') as HTMLButtonElement
  addZero.addEventListener('click', () => { polezero.addZero() })

  if (!mutable) {
    addZero.style.display = 'none'
    addPole.style.display = 'none'
  }
}

function createBodePlotView() {
  bode = new BodePlot('#bode-plot')
}

function createEquationView() {
  equationInput = document.querySelector('#equation-input') as HTMLTextAreaElement
  equationRender = document.querySelector('#equation-render')

  // Load the equation from local storage, if it's there.
  const savedEquation = localStorage.equation
  if (savedEquation) {
    equationInput.value = savedEquation
  }

  const equationButton = document.querySelector('#equation button')
  let isEdit = true
  onEditChanged()

  equationButton.addEventListener('click', onEditChanged)

  function onEditChanged() {
    isEdit = !isEdit
    equationRender.style.display = isEdit ? 'none' : 'block'
    equationInput.style.display = isEdit ? 'block' : 'none'
    equationButton.innerText = isEdit ? 'Save' : 'Edit'

    if (!isEdit) {
      let equation = equationInput.value.trim()
      onEquationChanged(equation)
    }
  }
}

function createFilterWizardView() {
  const filterWizard = new FilterWizard('#filter-wizard')
  filterWizard.on('transferfunctionchange', onFilterWizardTF)
}

function createAudioVisualizerView() {
  const visualizer = new AudioVisualizer('#audio-visualizer', player.getAnalyser())
}

function onEquationChanged(equation) {
  const tfunc = new TransferFunction(equation)
  window.tfunc = tfunc
  if (!tfunc.isValid()) {
    console.error('Invalid transfer function specified.')
    // Show a UI error.
    const dialog = document.querySelector('dialog#error-tfunc')
    dialog.showModal()
    dialog.querySelector('.close').addEventListener('click', () => {
      dialog.close()
    }, {once: true})
    return
  }
  updateTransferFunction(tfunc)
}

function onPoleZeroTF(tfunc: TransferFunction) {
  updateTransferFunction(tfunc)
}

function onFilterWizardTF(tfunc: TransferFunction) {
  updateTransferFunction(tfunc)
}

function updateTransferFunction(tfunc: TransferFunction) {
  if (!tfunc.isStable()) {
    console.error('Unstable transfer function. Aborting!')
    // Show a UI error.
    const dialog = document.querySelector('dialog#error-unstable')
    dialog.showModal()
    dialog.querySelector('.close').addEventListener('click', () => {
      dialog.close()
    }, {once: true})
    return
  }
  if (player) {
    player.updateIIRFilter(tfunc.getFeedForward(), tfunc.getFeedBack())
  }
  if (renderer) {
    renderer.setTransferFunction(tfunc)
  }
  if (polezero) {
    polezero.setTransferFunction(tfunc)
  }
  if (bode) {
    bode.setTransferFunction(tfunc)
  }

  // Update the equation view.
  const equation = tfunc.toString()
  if (equationInput) {
    equationInput.value = equation
  }
  if (equationRender) {
    const equationHuman = tfunc.toString(3)
    equationRender.innerHTML = `\`H(z) = ${equationHuman}\``
    MathJax.Hub.Config({
      "HTML-CSS": {
        availableFonts: [],
        webFont: 'TeX',
      }
    });
    MathJax.Hub.Queue(['Typeset', MathJax.Hub, equationRender]);
  }

  // Update the URL.
  let params = new URLSearchParams(location.search)
  params.set('equation', equation)
  history.replaceState({}, '', `${location.pathname}?${params}`)

  // Save the equation for the future.
  localStorage.equation = equation
}

function onVisibilityChange() {
  if (player) {
    if (document.hidden) {
      player.stop()
    } else {
      player.play()
    }
  }
}

window.addEventListener('load', onLoad)
window.addEventListener('visibilitychange', onVisibilityChange)



// WEBPACK FOOTER //
// ./src/index.ts